import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';

import { useAuth } from './contexts/AuthContext';
import { type AdminRole, hasRouteAccess, isAdminPanelRole, ROUTE_ROLES } from './constants/rbac';

import Layout from './components/layout/Layout';
import StudentLayout from './components/layout/StudentLayout';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Eager: entry-point pages that render immediately for unauthenticated users
import LandingPage from './pages/landing/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import AdminLoginPage from './pages/auth/AdminLoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Lazy: all other pages — loaded on demand when their route matches
const AdminPortalPage = lazy(() => import('./pages/admin/AdminPortalPage'));
const HostelPortfolioPage = lazy(() => import('./pages/admin/HostelPortfolioPage'));
const AdminBookingReviewPage = lazy(() => import('./pages/admin/AdminBookingReviewPage'));
const BallotingAllocationsPage = lazy(() => import('./pages/admin/BallotingAllocationsPage'));
const BallotingResultsPreviewPage = lazy(() => import('./pages/admin/BallotingResultsPreviewPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const BookingConfirmationPage = lazy(() => import('./pages/booking/BookingConfirmationPage'));
const BookingLandingPage = lazy(() => import('./pages/booking/BookingLandingPage'));
const BookingPaymentPage = lazy(() => import('./pages/booking/BookingPaymentPage'));
const BookingReviewPage = lazy(() => import('./pages/booking/BookingReviewPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const FacilitySetupPage = lazy(() => import('./pages/facilities/FacilitySetupPage'));
const PaymentsPage = lazy(() => import('./pages/payments/PaymentsPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const BankStatementsPage = lazy(() => import('./pages/bank-statements/BankStatementsPage'));
const BankStatementDetailPage = lazy(() => import('./pages/bank-statements/BankStatementDetailPage'));
const StudentImportPage = lazy(() => import('./pages/student-import/StudentImportPage'));
const StudentBookingsPage = lazy(() => import('./pages/student/StudentBookingsPage'));
const StudentDashboardPage = lazy(() => import('./pages/student/StudentDashboardPage'));
const StudentProfilePage = lazy(() => import('./pages/student/StudentProfilePage'));
const StudentSupportPage = lazy(() => import('./pages/student/StudentSupportPage'));
const SupportTicketsPage = lazy(() => import('./pages/admin/SupportTicketsPage'));
const StaffAccessPage = lazy(() => import('./pages/admin/StaffAccessPage'));
const StudentsPage = lazy(() => import('./pages/students/StudentsPage'));
const BlacklistPage = lazy(() => import('./pages/admin/BlacklistPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));

const PageFallback = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

type AdminAccessProps = {
  allowedRoles: readonly AdminRole[];
  children: ReactNode;
};

function RequireAdminAccess({ allowedRoles, children }: AdminAccessProps) {
  const { user } = useAuth();

  if (!user || !isAdminPanelRole(user.role)) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!hasRouteAccess(user.role, allowedRoles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function RequireStudentBookingAuth() {
  const location = useLocation();
  const redirect = `${location.pathname}${location.search}`;

  return (
    <Navigate
      to={`/login?redirect=${encodeURIComponent(redirect)}`}
      replace
    />
  );
}

function PublicRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* Hostel details are public; login is required at the review/payment steps */}
        <Route path="/booking/reserve" element={<BookingLandingPage />} />
        <Route path="/booking/review" element={<RequireStudentBookingAuth />} />
        <Route path="/booking/payment" element={<RequireStudentBookingAuth />} />
        <Route path="/support" element={<StudentSupportPage />} />
        <Route path="/admin" element={<AdminPortalPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function AdminRoutes() {
  return (
    <Layout>
      <Suspense fallback={<PageFallback />}>
        <Routes>
        <Route
          path="/"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.dashboard}>
              <DashboardPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.dashboard}>
              <DashboardPage />
            </RequireAdminAccess>
          }
        />
        <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
        <Route path="/admin/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/student/dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/student/profile" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/students"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.students}>
              <StudentsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/blacklist"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.blacklist}>
              <BlacklistPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/students/blacklist"
          element={<Navigate to="/blacklist" replace />}
        />
        <Route
          path="/student-import"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.studentImport}>
              <StudentImportPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/facilities"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.facilities}>
              <FacilitySetupPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/hostels"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.hostels}>
              <HostelPortfolioPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/hostels/:hostelId/structure"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.hostels}>
              <FacilitySetupPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/booking-reviews"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.bookingReviews}>
              <AdminBookingReviewPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/allocations"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.allocations}>
              <BallotingAllocationsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/allocations/history"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.allocations}>
              <BallotingAllocationsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/allocations/rules"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.allocations}>
              <BallotingAllocationsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/allocations/preview"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.allocations}>
              <BallotingResultsPreviewPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/payments"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.payments}>
              <PaymentsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/bank-statements"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.bankStatements}>
              <BankStatementsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/bank-statements/:id"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.bankStatements}>
              <BankStatementDetailPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/support-tickets"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.supportTickets}>
              <SupportTicketsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.settings}>
              <SettingsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/admin/staff-access"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.staffAccess}>
              <StaffAccessPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.reports}>
              <ReportsPage />
            </RequireAdminAccess>
          }
        />
        <Route
          path="/occupancy"
          element={
            <RequireAdminAccess allowedRoles={ROUTE_ROLES.occupancyGrid}>
              <CalendarPage />
            </RequireAdminAccess>
          }
        />
        <Route path="/support" element={<Navigate to="/support-tickets" replace />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/register" element={<Navigate to="/dashboard" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function StudentRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/booking/reserve" element={<BookingLandingPage />} />
      <Route path="/booking/review" element={<BookingReviewPage />} />
      <Route path="/booking/payment" element={<BookingPaymentPage />} />
      <Route path="/booking/confirmation" element={<BookingConfirmationPage />} />
      <Route path="/support" element={<Navigate to="/student/support" replace />} />

      {/* Student portal — wrapped in shared StudentLayout (sidebar + auth guards) */}
      <Route path="/student" element={<StudentLayout />}>
        <Route path="dashboard" element={<StudentDashboardPage />} />
        <Route path="bookings" element={<StudentBookingsPage />} />
        <Route path="profile" element={<StudentProfilePage />} />
        <Route path="support" element={<StudentSupportPage />} />
        <Route index element={<Navigate to="/student/dashboard" replace />} />
      </Route>

      {/* Admin entry points stay reachable for signed-in students (e.g. staff testing a student account) */}
      <Route path="/admin" element={<AdminPortalPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/login" element={<Navigate to="/student/dashboard" replace />} />
      <Route path="/register" element={<Navigate to="/student/dashboard" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="*" element={<Navigate to="/student/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <PublicRoutes />;
  }

  if (isAdminPanelRole(user.role)) {
    return <AdminRoutes />;
  }

  if (user.role === 'STUDENT') {
    return <StudentRoutes />;
  }

  return <PublicRoutes />;
}

export default App;
